/**
 * Public /enrol/sole-trader landing — copy-paste-friendly for Facebook /
 * Instagram / referrals. Outcome headline, three benefits, single CTA that
 * opens the multi-step ClientOps enrolment wizard inline.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import Brand from "@/components/Brand";
import EnrolWizard from "@/components/EnrolWizard";

const PLAN = { label: "Sole Trader Tax Compliance", base_nzd: 75 };

export default function EnrolSoleTrader() {
    useEffect(() => {
        const prev = document.title;
        document.title = "Sole Trader Tax — $75+GST/mo · NZ Taxation Limited";
        return () => { document.title = prev; };
    }, []);

    return (
        <div className="min-h-screen bg-[#F5F4EF]">
            <header className="mx-auto max-w-5xl px-4 py-6 flex items-center justify-between">
                <Brand size="sm" data-testid="enrol-brand-home" />
                <Link to="/login" className="text-[13px] text-[#5E636E] hover:text-[#0F1115]" data-testid="enrol-signin-link">Sign in</Link>
            </header>

            <main className="mx-auto max-w-5xl px-4 pb-16">
                <section className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-start">
                    <div>
                        <div className="inline-flex items-center gap-2 rounded-full bg-[#0F1115] text-white px-3 py-1 text-[11px] uppercase tracking-wider">
                            <span>NZ Taxation Limited t/a ClientOps</span>
                            <span className="opacity-60">·</span>
                            <span>Sole Trader</span>
                        </div>
                        <h1 className="mt-4 text-[32px] md:text-[42px] leading-tight font-semibold text-[#0F1115]">
                            Sole trader tax, sorted.<br />
                            <span className="text-[#0F1115]">NZ$75 + GST</span> a month.
                        </h1>
                        <p className="mt-3 text-[15px] leading-6 text-[#1F2328]">
                            Your annual IR3 filed on time. Your GST returns handled if you're registered. Your IRD correspondence taken off your plate. All-in, one flat monthly fee.
                        </p>

                        <ul className="mt-6 space-y-3 text-[14px] text-[#1F2328]">
                            <li className="flex items-start gap-3" data-testid="enrol-st-benefit-1">
                                <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-[#0F1115] text-white text-[12px] leading-5 text-center">✓</span>
                                <span><b>Annual IR3 filed for you</b> — no last-minute March scramble, ever again.</span>
                            </li>
                            <li className="flex items-start gap-3" data-testid="enrol-st-benefit-2">
                                <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-[#0F1115] text-white text-[12px] leading-5 text-center">✓</span>
                                <span><b>GST returns included</b> while you're GST-registered — 2-monthly or 6-monthly.</span>
                            </li>
                            <li className="flex items-start gap-3" data-testid="enrol-st-benefit-3">
                                <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-[#0F1115] text-white text-[12px] leading-5 text-center">✓</span>
                                <span><b>Free invoicing via Prosaic</b> — send professional invoices from your phone, we sync the data straight into your tax return. No extra fee.</span>
                            </li>
                            <li className="flex items-start gap-3" data-testid="enrol-st-benefit-4">
                                <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-[#0F1115] text-white text-[12px] leading-5 text-center">✓</span>
                                <span><b>Everything in ClientOps</b> — upload receipts from your phone, track jobs, chat with your agent, done.</span>
                            </li>
                        </ul>

                        <div className="mt-8 text-[12px] text-[#5E636E]">
                            Signed up in under 5 minutes. A NZ Taxation team member calls to welcome you before your first invoice. Cancellable in writing with 30 days notice.
                        </div>
                    </div>

                    <div id="enrol">
                        <EnrolWizard service="sole_trader" plan={PLAN} />
                    </div>
                </section>

                <section className="mt-14 border-t border-[#E4E4E7] pt-8 grid grid-cols-1 md:grid-cols-3 gap-6 text-[13px] text-[#1F2328]">
                    <div>
                        <div className="font-medium text-[#0F1115] mb-2">What's included</div>
                        <ul className="space-y-1 text-[13px]">
                            <li>· IR3 income tax return (annual)</li>
                            <li>· GST returns (while registered)</li>
                            <li>· <b>Prosaic invoicing app — free</b>, no extra subscription</li>
                            <li>· IRD correspondence as your agent</li>
                            <li>· Deduction advisory — home office, vehicle, tools</li>
                        </ul>
                    </div>
                    <div>
                        <div className="font-medium text-[#0F1115] mb-2">What's not included</div>
                        <ul className="space-y-1 text-[13px]">
                            <li>· Ongoing bookkeeping / Xero coding</li>
                            <li>· Business advisory or forecasting</li>
                            <li>· Company setup or restructures</li>
                            <li className="text-[#5E636E]">(Ask us for a quote — we can add these on)</li>
                        </ul>
                    </div>
                    <div>
                        <div className="font-medium text-[#0F1115] mb-2">How it works</div>
                        <ol className="space-y-1 text-[13px] list-decimal ml-4">
                            <li>Fill in the wizard →</li>
                            <li>Sign the Authority to Act (typed name)</li>
                            <li>We call to welcome you within 24 hours</li>
                            <li>Your first invoice arrives once you're set up</li>
                        </ol>
                    </div>
                </section>
            </main>

            <footer className="border-t border-[#E4E4E7] bg-white">
                <div className="mx-auto max-w-5xl px-4 py-6 text-[12px] text-[#5E636E] flex flex-wrap gap-x-4 gap-y-2 justify-between">
                    <span>© NZ Taxation Limited t/a ClientOps</span>
                    <Link to="/privacy" className="hover:text-[#0F1115]">Privacy</Link>
                    <Link to="/support" className="hover:text-[#0F1115]">Support</Link>
                </div>
            </footer>
        </div>
    );
}
